import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Formik, Field, Form, ErrorMessage, FieldArray, FormikHelpers, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Col, Row, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import {
    ReactSelect,
    TableHeader,
    SpinnerLoader,
    Spinner,
    MultiSwitchField,
    TextareaInputWithCounter,
    PriceInput,
    InfoBanner,
    WithRoleAccess,
} from "@services/ui-components";
import { ApolloError, useReactiveVar } from "@apollo/client";
import { RequestInputService, ServiceModalWrapper } from "@me-pos/service-modal";
import i18next from "i18next";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import {
    CompanyAccess,
    MenuCategoryInput,
    MenuItemInput,
    MenuItemPortionInput,
    MenuTypeType,
} from "@me-team/host/main/graphql/types";
import CurrentMenuItemFormEffects from "./CurrentMenuItemFormEffects";
import { CategoryInterface } from "../../Products/types";
import { IPickedCategoryNames } from "../MenuItemsPage/constants";
import { normalizeCategoryData } from "../../../services/servicesMeMenuCategories/normalizeCategoryData";
import CategoryMeMenu from "../../../services/servicesMeMenuCategories/CategoryMeMenu";
import ErrorService from "../../../services/ErrorService";
import { cloneNameToApi, cloneNameToDisplay } from "../utils/cloneNameUtils";
import ModalCloneMenuItem from "../MenuItemsPage/modals/ModalCloneMenuItem";
import ConfirmDisabledMenuTypesModal from "./ConfirmDisabledMenuTypesModal";
import { getCookingTimeOptions, getEmptyMenuItemForm } from "./constants";
import { getConflictingMenuTypesFromAvailabilityQuery, getDisabledMenuTypes } from "./menuTypeAvailability";
import { Option, OptionString } from "../../../interfaces/interfaces";
import {
    useCheckMenuItemAvailabilityForMenuTypeLazyQuery,
    useCreateMenuCategoryForUpdateMenuItemMutation,
    useGetCurrentMenuItemForUpdateQuery,
    useGetDishSupplementByIdLazyQuery,
    useGetDishSupplementsNameAndIdQuery,
    useGetMenuAllergensForUpdateMenuItemQuery,
    useGetMenuCategoriesForUpdateMenuItemsQuery,
    useGetMenuMarkersForUpdateMenuItemQuery,
    useGetMenuTypesForUpdateMenuItemQuery,
    useGetUnitsForUpdateMenuItemQuery,
    useUpdateMenuItemMutation,
} from "@me-team/host/main/updateMenuItem/graphql/updateMenuItem.hooks";
import CategoryIconPickerWithLabel from "../components/CategoryIconPicker/CategoryIconPickerWithLabel";
import { useMenuCategoryIcons } from "../utils/hooks/useMenuCategoryIcons";
import {
    DishForTree,
    PartialDishSupplement,
    PartialMenuCategory,
    PartialMenuItem,
} from "../../../interfaces/meMenuInterfaces/interfaces";
import TextInput from "@services/ui-components/src/TextInput/TextInput";
import PortionVolumeInput from "./inputs/PortionVolumeInput";
import { menuItemValidationSchema } from "../DishCreatePage/constants";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { SupplementsGroupModalState } from "../SupplementsForDishesPage/SupplementsForDishesPage";
import {
    useCreateMenuSupplementMutation,
    useGetMenuItemsForSupplementsQuery,
    useUpdateMenuSupplementMutation,
} from "@me-team/host/main/dishSupplements/graphql/dishSupplements.hooks";
import { useRestoreMenuItemMutation } from "@me-team/host/main/menuItems/graphql/menuItems.hooks";
import { mapDishesFromBackendToTreeDishes } from "../SupplementsForDishesPage/utils/dishesInputServices/mapDishesFromBackendToTreeDishes";
import SupplementsGroupModal, {
    SupplementsGroupFormValues,
    SupplementsGroupSubmitValues,
} from "../SupplementsForDishesPage/modals/SupplementsGroupModal/SupplementsGroupModal";
import { buildSupplementInput } from "../SupplementsForDishesPage/utils/buildSupplementInput";
import DishSupplementsInput from "../DishCreatePage/components/DishSupplementsInput";
import AddSupplementsToDishModal from "../DishCreatePage/modals/AddSupplementsToDishModal/AddSupplementsToDishModal";
import { getGraphqlErrorCategory } from "../SupplementsForDishesPage/utils/helpers";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import MenuLanguageVersionSelectContainer from "../components/LanguageVersionSelect/MenuLanguageVersionSelectContainer";
import { useMenuLanguagesData } from "../utils/hooks/useMenuLanguagesData";
import { buildPrimaryLanguageVm, buildSelectedLanguageVm } from "../SupplementsForDishesPage/utils/languageVm";
import { buildTranslationTextVm } from "../utils/translationVersions/buildTranslationTextVm";
import { getCopyOriginProps } from "@me-team/common/src/components/Me-menu/utils/translationVersions/getCopyOriginProps";
import CopyOriginButton from "@me-team/common/src/components/Me-menu/components/CopyOriginButton/CopyOriginButton";
import { TranslationLockedFieldsInfoBanner } from "@me-team/common/src/components/Me-menu/components/TranslationLockedFieldsInfoBanner/TranslationLockedFieldsInfoBanner";
import TranslationMissingDot from "../components/LanguageVersionSelect/TranslationMissingDot";
import { getMenuTypeEnums, getMenuTypeEnumOptionsBySlug } from "../AllMenusPage/menuEnumMappings";
import { useToast } from "@me-team/common/src/hooks/useToast";
import ToastComponent from "@me-team/common/src/components/ui-components/ToastComponent/ToastComponent";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

export interface MenuItemFormData {
    name: string;
    types: MenuTypeType[];
    categories: number[];
    description?: string;
    composition?: string;
    unit: number;
    portions: MenuItemPortionInput[];
    cookingTime?: string;
    isLongCookingTime: boolean;
    markers?: number[];
    allergens: number[];
    supplements: number[];
    image?: string;
    isUpdateImage: boolean;
    deleted: boolean;
}

export type DishSubmitOutcome = "pending" | "saved" | "cancelled" | "error";

type AvailabilityConfirmState = {
    show: boolean;
    menuTypeLabels: string[];
};

const initialAvailabilityConfirmState: AvailabilityConfirmState = {
    show: false,
    menuTypeLabels: [],
};

interface DishUpdatePageProps {
    refetchMenuItems?: () => void;
}

const DishUpdatePage: React.FC<DishUpdatePageProps> = ({ refetchMenuItems }) => {
    const { t } = useTranslation();
    const translationTooltip = t("Edit the language version");
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const isRestoreMode: boolean = location.pathname.includes("/menu/restore-dish");
    const { dishId, branchId } = useParams();
    const submitText = isRestoreMode ? t("Restore") : t("Save");
    const submitLoadingText = isRestoreMode ? t("Restoring...") : t("Saving...");

    const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
    const [currentDishId, setCurrentDishId] = useState<number>(null);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const canEditDish = useRoleAccess(CompanyAccess.MenuDishEdit);
    const canDeleteRestoreDish = useRoleAccess(CompanyAccess.MenuDishDeleteRestore);
    const canCloneDish = useRoleAccess(CompanyAccess.MenuDishClone);
    const canCreateDishCategory = useRoleAccess(CompanyAccess.MenuDishCategoryCreate);
    const canCreateSupplement = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsCreate);
    const canEditSupplement = useRoleAccess(CompanyAccess.MenuOptionsAndSupplementsEdit);
    const buttonCategoryRef = useRef<HTMLButtonElement>(null);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [isMeMenuCategoryModalOpen, setIsMeMenuCategoryModalOpen] = useState<boolean>(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [categoryList, setCategoryList] = useState<CategoryInterface[]>(null);
    const [categoryInput, setCategoryInput] = useState(null);
    const [pickedCategoryIds, setPickedCategoryIds] = useState<number[]>([]);
    const [pickedCategoryNames, setPickedCategoryNames] = useState<IPickedCategoryNames[]>([]);
    const initializedCategoriesKeyRef = useRef<string | null>(null);
    const cookingTimeOptions: OptionString[] = getCookingTimeOptions(t);
    const emptyMenuItemForm = getEmptyMenuItemForm(t);
    const [showSupplementMenu, setShowSupplementMenu] = useState(false);
    const [modalState, setModalState] = useState<SupplementsGroupModalState>({
        isOpen: false,
        mode: "create",
        group: null,
    });
    const [listAddOnsRefetch, setListAddOnsRefetch] = useState<(() => Promise<unknown>) | null>(null);
    const [returnToAddSupplementsModal, setReturnToAddSupplementsModal] = useState(false);
    const languageSwitchRequestRef = useRef<((nextCode: string) => void) | null>(null);
    const preventNavigateOnSaveRef = useRef(false);
    const submitOutcomeRef = useRef<DishSubmitOutcome>("pending");
    const formikRef = useRef<FormikProps<MenuItemFormData>>(null);
    const availabilityConfirmResolverRef = useRef<((confirmed: boolean) => void) | null>(null);
    const [availabilityConfirmState, setAvailabilityConfirmState] = useState<AvailabilityConfirmState>(
        initialAvailabilityConfirmState
    );
    const [createMenuCategory] = useCreateMenuCategoryForUpdateMenuItemMutation();
    const [updateMenuItem] = useUpdateMenuItemMutation();
    const [restoreMenuItem] = useRestoreMenuItemMutation();
    const [updateMenuSupplement] = useUpdateMenuSupplementMutation();
    const [createMenuSupplement] = useCreateMenuSupplementMutation();
    const [checkMenuItemAvailabilityForMenuType] = useCheckMenuItemAvailabilityForMenuTypeLazyQuery({
        fetchPolicy: "network-only",
    });

    const [getSupplementById] = useGetDishSupplementByIdLazyQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const {
        loading: menuLanguagesLoading,
        primaryLanguage,
        languages,
    } = useMenuLanguagesData(Number(currentCompanyId));
    const primaryLanguageVm = useMemo(
        () => buildPrimaryLanguageVm(primaryLanguage, languages),
        [primaryLanguage, languages]
    );
    const selectedLanguageVm = useMemo(
        () => buildSelectedLanguageVm(selectedLangVersionCode, primaryLanguageVm, languages),
        [selectedLangVersionCode, primaryLanguageVm, languages]
    );

    const isTranslationMode = !!(
        selectedLangVersionCode &&
        primaryLanguage?.isoCode &&
        selectedLangVersionCode !== primaryLanguage?.isoCode
    );

    const { data: currentMenuItemData, loading: currentMenuItemLoading } = useGetCurrentMenuItemForUpdateQuery({
        variables: {
            dishId: Number(dishId),
            companyId: Number(currentCompanyId),
            locale: selectedLangVersionCode,
            isDeletedSupplements: false,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        context: {
            errorType: "global",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const menuItem = currentMenuItemData?.user?.company?.menuItems?.items?.[0];

    const { data: unitsData, loading: unitsLoading } = useGetUnitsForUpdateMenuItemQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const unitOptions =
        unitsData?.relations?.units
            ?.filter((unit) => [9, 10, 6, 7].includes(unit.id))
            ?.map((unit) => ({
                label: unit.name,
                value: unit.id,
            })) || [];

    const initialValues: MenuItemFormData = useMemo(() => {
        if (!menuItem) {
            return emptyMenuItemForm;
        }
        return {
            name: cloneNameToDisplay(menuItem.name, t),
            types: getMenuTypeEnums(menuItem.types),
            categories: menuItem.categories.map((category) => category.id),
            description: menuItem.description || "",
            composition: menuItem.composition || "",
            unit: menuItem.unit?.id || null,
            portions:
                menuItem.portions?.length > 0
                    ? menuItem.portions.map((portion) => ({
                          id: portion.id,
                          name: portion.name,
                          volume: portion.volume,
                          price: portion.price,
                      }))
                    : [],
            cookingTime: menuItem.cookingTime || null,
            isLongCookingTime: menuItem.isLongCookingTime,
            markers: menuItem.markers.map((marker) => marker.id),
            allergens: menuItem.allergens.map((allergen) => allergen.id),
            supplements:
                menuItem.supplements?.length > 0 ? menuItem.supplements.map((supplement) => supplement.id) : null,
            image: null,
            isUpdateImage: false,
            deleted: false,
        };
    }, [menuItem, t, emptyMenuItemForm]);

    const {
        data: meMenucategoriesData,
        refetch: refetchCategories,
        loading: meMenuCategoryLoading,
    } = useGetMenuCategoriesForUpdateMenuItemsQuery({
        variables: {
            companyId: Number(currentCompanyId),
            itemsPerPage: 1000,
            isDeleted: false,
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        context: {
            errorType: "local",
        },
        onCompleted: (data) => {
            const normalizedItems = normalizeCategoryData(data);
            setCategoryList(CategoryMeMenu.categoryModuleCreator(normalizedItems, null, null));
            setCategoryInput(RequestInputService.createServiceCategoryInput(null, null, normalizedItems));
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const categoryListData: PartialMenuCategory[] =
        meMenucategoriesData?.user?.company?.meMenuCategoriesPaginated?.categories;

    const categoryIcons = useMenuCategoryIcons(currentCompanyId);
    const handleSubmitCreateCategory = async (variables: MenuCategoryInput) => {
        if (!canCreateDishCategory) return;

        const input: MenuCategoryInput = {
            ...variables,
            parentCategoryId: typeof variables.parentCategoryId === "number" ? variables.parentCategoryId : null,
        };
        try {
            await createMenuCategory({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    input,
                    locale: primaryLanguageVm?.code,
                },
                context: {
                    errorType: "local",
                },
                onCompleted: (data) => {
                    data && setCurrentCategory(data.createMenuCategory);
                },
                onError: (error) => {
                    setIsErrorModalOpen(ErrorService.errorHandling(error));
                },
            });
        } catch (error) {
            console.error(error);
        }
    };

    const { data: menuAllergensData, loading: allergensLoading } = useGetMenuAllergensForUpdateMenuItemQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const { data: menuMarkersData, loading: markersLoading } = useGetMenuMarkersForUpdateMenuItemQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const { data: menuTypesData, loading: menuTypesLoading } = useGetMenuTypesForUpdateMenuItemQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });

    const {
        data: dishSupplementsNameAndIdData,
        loading: dishSupplementsNameAndIdLoading,
        refetch: dishSupplementsNameAndIdRefetch,
    } = useGetDishSupplementsNameAndIdQuery({
        variables: {
            companyId: Number(currentCompanyId),
            isDeleted: false,
            onlyParent: true,
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        context: {
            errorType: "local",
        },
        fetchPolicy: "network-only",
        onError: (error) => {
            console.error(error);
        },
    });

    const supplementsNameAndId: PartialDishSupplement[] =
        dishSupplementsNameAndIdData?.user?.company?.menuSupplementsList;
    const supplementGroupsForInput = useMemo(
        () =>
            (supplementsNameAndId ?? []).map((supplement) => ({
                id: supplement.id,
                name: supplement.groupName,
                nameOrigin: supplement.groupNameOrigin,
            })),
        [supplementsNameAndId]
    );

    const menuTypeLabelsByEnum = useMemo(
        () =>
            Object.fromEntries(
                getMenuTypeEnumOptionsBySlug(menuTypesData?.menuTypes, (name) => t(name)).map((option) => [
                    option.value,
                    option.label,
                ])
            ) as Partial<Record<MenuTypeType, string>>,
        [menuTypesData?.menuTypes, t]
    );

    const handleRemoveCategory = (categoryId: number) => {
        setPickedCategoryIds((prev: number[]) => prev.filter((id) => id !== categoryId));
        setPickedCategoryNames((prev) => prev.filter((cat) => cat.id !== categoryId));
    };

    const handleLabelClick = (event: React.MouseEvent<HTMLLabelElement>) => {
        event.preventDefault();
        if (buttonCategoryRef.current) {
            buttonCategoryRef.current.focus();
        }
    };

    const handleCloneClick = (id: number) => {
        if (!canCloneDish) return;
        setCurrentDishId(id);
        setShowCloneModal(true);
    };

    const resolveAvailabilityConfirmation = useCallback((confirmed: boolean) => {
        const resolve = availabilityConfirmResolverRef.current;
        availabilityConfirmResolverRef.current = null;
        setAvailabilityConfirmState({
            show: false,
            menuTypeLabels: [],
        });
        resolve?.(confirmed);
    }, []);

    const requestAvailabilityConfirmation = useCallback(
        (menuTypes: MenuTypeType[]) =>
            new Promise<boolean>((resolve) => {
                availabilityConfirmResolverRef.current = resolve;
                setAvailabilityConfirmState({
                    show: true,
                    menuTypeLabels: menuTypes.map((type) => menuTypeLabelsByEnum[type] ?? type),
                });
            }),
        [menuTypeLabelsByEnum]
    );

    useEffect(() => {
        return () => {
            availabilityConfirmResolverRef.current?.(false);
            availabilityConfirmResolverRef.current = null;
        };
    }, []);

    useEffect(() => {
        initializedCategoriesKeyRef.current = null;
    }, [selectedLangVersionCode]);

    useEffect(() => {
        if (!menuItem || !selectedLangVersionCode || currentMenuItemLoading) return;

        const categoriesInitKey = `${menuItem.id}:${selectedLangVersionCode}`;
        if (initializedCategoriesKeyRef.current === categoriesInitKey) return;

        const initialPicked: IPickedCategoryNames[] = (menuItem.categories ?? []).map((category) => ({
            id: category.id,
            name: category.name,
            parentName: category.parentCategory?.name || null,
            nameOrigin: category.nameOrigin ?? null,
            parentNameOrigin: category.parentCategory?.nameOrigin ?? null,
        }));

        setPickedCategoryIds(initialPicked.map((category) => category.id));
        setPickedCategoryNames(initialPicked);
        initializedCategoriesKeyRef.current = categoriesInitKey;
    }, [menuItem, selectedLangVersionCode, currentMenuItemLoading]);

    const { data: dishesFromCategoriesData, loading: dishesFromCategoriesLoading } = useGetMenuItemsForSupplementsQuery(
        {
            variables: {
                companyId: Number(currentCompanyId),
                itemsPerPage: 1000001,
                isDeleted: false,
                locale: selectedLangVersionCode,
            },
            skip: !currentCompanyId || !selectedLangVersionCode,
            context: {
                errorType: "local",
            },
            onError: (error: ApolloError) => {
                setIsErrorModalOpen(ErrorService.errorHandling(error));
            },
        }
    );

    const dishesFromCategories: PartialMenuItem[] = dishesFromCategoriesData?.user?.company?.menuItems?.items;

    const dishesData: DishForTree[] = useMemo(
        () => mapDishesFromBackendToTreeDishes(dishesFromCategories),
        [dishesFromCategories]
    );

    const openCreateModal = (fromAddModal = false) => {
        if (!canCreateSupplement) return;

        if (fromAddModal) {
            setReturnToAddSupplementsModal(true);
        }
        setModalState({
            isOpen: true,
            mode: "create",
            group: null,
        });
    };

    const openEditModal = async (groupId: number) => {
        if (!canEditSupplement) return;
        if (!groupId || !currentCompanyId || !selectedLangVersionCode) return;
        const { data } = await getSupplementById({
            variables: {
                companyId: Number(currentCompanyId),
                supplementId: groupId,
                locale: selectedLangVersionCode,
            },
        });
        const supplementGroup = data?.user?.company?.menuSupplementsList?.[0];
        if (supplementGroup) {
            setModalState({
                isOpen: true,
                mode: "edit",
                group: supplementGroup,
            });
        }
    };

    const handleCloseModal = () => {
        setModalState((prev) => ({
            ...prev,
            isOpen: false,
        }));

        if (returnToAddSupplementsModal) {
            setShowSupplementMenu(true);
            setReturnToAddSupplementsModal(false);
        }
    };

    const handleSubmitSupplementsGroupModal = async (
        values: SupplementsGroupSubmitValues,
        formikHelpers: FormikHelpers<SupplementsGroupFormValues>
    ) => {
        if (!currentCompanyId) return;

        const companyId = Number(currentCompanyId);
        const currentGroup = modalState.group;

        formikHelpers.setFieldError("groupName", undefined);

        try {
            if (modalState.mode === "create") {
                if (!canCreateSupplement) return;

                const input = buildSupplementInput(values);
                await createMenuSupplement({
                    variables: {
                        companyId,
                        branchId: Number(branchId),
                        input,
                        locale: primaryLanguageVm?.code,
                    },
                    context: { errorType: "local" },
                });
            }

            if (modalState.mode === "edit" && currentGroup) {
                if (!canEditSupplement) return;

                const input = buildSupplementInput(values);
                await updateMenuSupplement({
                    variables: {
                        companyId,
                        branchId: Number(branchId),
                        dishId: currentGroup.id,
                        input,
                        locale: selectedLangVersionCode,
                    },
                    context: { errorType: "local" },
                });
            }

            await dishSupplementsNameAndIdRefetch();

            if (typeof listAddOnsRefetch === "function") {
                await listAddOnsRefetch();
            }

            handleCloseModal();
        } catch (error) {
            const apolloError = error as ApolloError;
            const errorCategory = getGraphqlErrorCategory(error);
            if (errorCategory === "menu_supplement.already_exists") {
                formikHelpers.setFieldError("groupName", t("A group with this name and options already exists"));
                return;
            }
            setIsErrorModalOpen(ErrorService.errorHandling(apolloError));
        }
    };

    const performUpdateMenuItem = useCallback(
        async (
            input: MenuItemInput,
            values: MenuItemFormData,
            { setErrors, resetForm }: FormikHelpers<MenuItemFormData>
        ) => {
            await updateMenuItem({
                variables: {
                    companyId: Number(currentCompanyId),
                    branchId: Number(branchId),
                    menuItemId: Number(dishId),
                    input,
                    locale: selectedLangVersionCode,
                },
                onCompleted: () => {
                    submitOutcomeRef.current = "saved";
                    if (preventNavigateOnSaveRef.current) {
                        preventNavigateOnSaveRef.current = false;
                        resetForm({ values });
                        return;
                    }
                    if (location.state?.menuId) {
                        navigate(`/menu/page/${location.state.menuId}/branch/${branchId}`, {
                            state: { text: t("Saved"), toast: true },
                        });
                    } else {
                        navigate(`/menu/dishes/branch/${branchId}`, { state: { text: t("Saved"), toast: true } });
                    }
                },
                onError: (error: ApolloError) => {
                    submitOutcomeRef.current = "error";
                    preventNavigateOnSaveRef.current = false;

                    const validationErrors = error?.graphQLErrors[0];
                    const validationErrorsCategory = validationErrors?.extensions?.category;
                    if (validationErrorsCategory === "menu_item.already_exists") {
                        setErrors({ name: t("A dish with this name already exists.") });
                    }
                    const validation = validationErrors?.extensions?.validation as {
                        image?: Array<{ message?: string }>;
                    };
                    const imageError = validation?.image?.[0]?.message?.replace(/\(\d+px\)/, "(width)");

                    if (imageError === "The image width is too small (width). Minimum width expected is 500px.") {
                        setErrors({ image: t("The image width is too small. Minimum width expected is 500px.") });
                    }
                },
            });
        },
        [
            branchId,
            currentCompanyId,
            dishId,
            location.state?.menuId,
            navigate,
            selectedLangVersionCode,
            t,
            updateMenuItem,
        ]
    );

    const handleSubmit = async (values: MenuItemFormData, formikHelpers: FormikHelpers<MenuItemFormData>) => {
        if (isRestoreMode ? !canDeleteRestoreDish : !canEditDish) return;

        submitOutcomeRef.current = "pending";

        if (isRestoreMode) {
            try {
                await restoreMenuItem({
                    variables: {
                        branchId: Number(branchId),
                        id: Number(dishId),
                    },
                    onCompleted: () => {
                        submitOutcomeRef.current = "saved";
                        navigate(`/menu/dishes/branch/${branchId}`, { state: { text: t("Restored"), toast: true } });
                    },
                    onError: () => {
                        submitOutcomeRef.current = "error";
                    },
                });
            } catch (error) {
                submitOutcomeRef.current = "error";
                console.error(error);
            }
            return;
        }

        const normalizedName = cloneNameToApi(values.name, t);
        const normalizedPortions: MenuItemPortionInput[] = values.portions.map((portion) => ({
            ...(typeof portion.id === "number" ? { id: portion.id } : {}),
            name: portion.name,
            volume: portion.volume,
            price: portion.price,
        }));

        const input = {
            name: normalizedName,
            types: values.types,
            categories: values.categories,
            unit: values.unit,
            portions: normalizedPortions,
            description: values.description,
            composition: values.composition,
            cookingTime: values.cookingTime,
            isLongCookingTime: values.isLongCookingTime,
            markers: values.markers,
            allergens: values.allergens,
            supplements: values.supplements?.length > 0 ? values.supplements.map((supplement) => supplement) : null,
            image: values.image,
            isUpdateImage: values.isUpdateImage || false,
        };
        if (values?.isUpdateImage === true) input.image = values.image;

        try {
            const initialTypes = formikRef.current?.initialValues?.types ?? initialValues.types;
            const disabledTypes = getDisabledMenuTypes(initialTypes, values.types);

            if (disabledTypes.length > 0) {
                const { data } = await checkMenuItemAvailabilityForMenuType({
                    variables: {
                        companyId: Number(currentCompanyId),
                        branchId: Number(branchId),
                        dishId: Number(dishId),
                    },
                });

                const conflictingTypes = getConflictingMenuTypesFromAvailabilityQuery(data, disabledTypes);

                if (conflictingTypes.length > 0) {
                    const isConfirmed = await requestAvailabilityConfirmation(conflictingTypes);

                    if (!isConfirmed) {
                        submitOutcomeRef.current = "cancelled";
                        preventNavigateOnSaveRef.current = false;
                        return;
                    }
                }
            }

            await performUpdateMenuItem(input, values, formikHelpers);
        } catch (error) {
            submitOutcomeRef.current = "error";
            preventNavigateOnSaveRef.current = false;
            throw error;
        }
    };

    return (
        <>
            <Formik<MenuItemFormData>
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={menuItemValidationSchema(t)}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ setFieldValue, isSubmitting, values, errors, touched }) => {
                    const nameVm = buildTranslationTextVm({
                        translatedValue: values.name,
                        originValue: cloneNameToDisplay(menuItem?.nameOrigin, t),
                        isTranslationMode,
                    });
                    const nameCopyOriginProps = getCopyOriginProps(nameVm);
                    const isNameCopyOriginButtonShown =
                        nameCopyOriginProps.show && Boolean(nameCopyOriginProps.originText.trim());

                    const descriptionVm = buildTranslationTextVm({
                        translatedValue: values.description,
                        originValue: menuItem?.descriptionOrigin,
                        isTranslationMode,
                    });

                    const compositionVm = buildTranslationTextVm({
                        translatedValue: values.composition,
                        originValue: menuItem?.compositionOrigin,
                        isTranslationMode,
                    });
                    return (
                        <>
                            <InfoBanner>
                                <p className="fs-7 mb-0">
                                    {t(
                                        "On this page, you can change the name, description, price, composition, category, and other parameters of a dish. Required fields are marked with an asterisk (*)."
                                    )}
                                </p>
                            </InfoBanner>
                            <TableHeader
                                title={isRestoreMode ? t("Restore dish") : t("Edit dish")}
                                paths={[
                                    { path: `/branches/${branchId}`, label: t("Branches") },
                                    { path: `/menu/dishes/branch/${branchId}`, label: t("List of dishes") },
                                    { path: "", label: isRestoreMode ? t("Restore dish") : t("Edit dish") },
                                ]}
                                buttons={
                                    <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                                        <Col
                                            md={6}
                                            lg={7}
                                            xl={6}
                                            xxl={5}
                                            className="mb-3 mb-xxl-0"
                                            style={{ minWidth: "223px" }}
                                        >
                                            <div className="position-relative">
                                                <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                                    {t("Language version")}
                                                </span>
                                                <MenuLanguageVersionSelectContainer
                                                    companyId={Number(currentCompanyId)}
                                                    onRequestLanguageChange={(nextCode) => {
                                                        if (languageSwitchRequestRef.current) {
                                                            languageSwitchRequestRef.current(nextCode);
                                                            return;
                                                        }
                                                        menuLanguageVersionVar(nextCode);
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                        <WithRoleAccess permission={CompanyAccess.MenuDishClone}>
                                            <Col md={6} lg={7} xl={6} xxl={5}>
                                                <Button
                                                    variant="outline-primary"
                                                    className="w-100"
                                                    onClick={() => handleCloneClick(+dishId)}
                                                >
                                                    {t("Clone dish")}
                                                </Button>
                                            </Col>
                                        </WithRoleAccess>
                                    </Row>
                                }
                                colSize={6}
                                btnColSize={6}
                            />

                            {isTranslationMode && <TranslationLockedFieldsInfoBanner />}

                            {currentMenuItemLoading ||
                            !menuItem ||
                            unitsLoading ||
                            allergensLoading ||
                            markersLoading ||
                            menuTypesLoading ||
                            menuLanguagesLoading ||
                            dishSupplementsNameAndIdLoading ||
                            dishesFromCategoriesLoading ? (
                                <Spinner />
                            ) : (
                                <>
                                    <Form>
                                        <CurrentMenuItemFormEffects
                                            pickedCategoryIds={pickedCategoryIds}
                                            languageSwitchRequestRef={languageSwitchRequestRef}
                                            preventNavigateOnSaveRef={preventNavigateOnSaveRef}
                                            submitOutcomeRef={submitOutcomeRef}
                                        />
                                        <Row className="mt-6">
                                            <Col lg={9} className="pe-lg-5">
                                                <h5 className="fs-24 text-capitalize mb-3 mb-lg-4">
                                                    {t("Basic information")}
                                                </h5>
                                                <div>
                                                    <div className="mb-3">
                                                        <Row className="align-items-center">
                                                            <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                                <label
                                                                    htmlFor="updateDish-name-field"
                                                                    className="fs-7 fw-bold required-label"
                                                                >
                                                                    {t("Dish name")}
                                                                </label>
                                                            </Col>
                                                            <Col sm={9} md={10}>
                                                                <div className="position-relative">
                                                                    <Field
                                                                        id="updateDish-name-field"
                                                                        name="name"
                                                                        placeholder={
                                                                            nameVm.placeholderText ?? t("Add dish name")
                                                                        }
                                                                        maxLength={100}
                                                                        className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}${
                                                                            isNameCopyOriginButtonShown
                                                                                ? " padding-placeholder-is-invalid padding-background-position-is-invalid"
                                                                                : ""
                                                                        }`}
                                                                    />
                                                                    {isNameCopyOriginButtonShown ? (
                                                                        <div className="position-absolute top-50 end-0 translate-middle-y me-2 d-flex align-items-center">
                                                                            <CopyOriginButton
                                                                                {...nameCopyOriginProps}
                                                                                onCopied={() =>
                                                                                    toggleShowToast(
                                                                                        t("Original text copied")
                                                                                    )
                                                                                }
                                                                            />
                                                                        </div>
                                                                    ) : null}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col sm={9} md={10} className="ms-auto">
                                                                <ErrorMessage name="name">
                                                                    {(msg) => (
                                                                        <div className="invalid-feedback d-block">
                                                                            {msg}
                                                                        </div>
                                                                    )}
                                                                </ErrorMessage>
                                                                <div className="fs-7 text-grey mt-1">
                                                                    {values.name?.length}/100
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </div>
                                                    <Row className="mb-3 py-lg-2">
                                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                            <label className="fs-7 fw-bold required-label d-inline">
                                                                {t("Available for")}
                                                            </label>
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        <span className="d-block w-100 text-start">
                                                                            {t(
                                                                                "Select which menu types this dish should be available for"
                                                                            )}
                                                                        </span>
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <i className="bi bi-info-circle ms-1 fs-7"></i>
                                                            </OverlayTrigger>
                                                        </Col>
                                                        <Col sm={9} md={10}>
                                                            <div className="d-flex justify-content-start flex-wrap gap-3">
                                                                <MultiSwitchField
                                                                    name="types"
                                                                    options={getMenuTypeEnumOptionsBySlug(
                                                                        menuTypesData?.menuTypes,
                                                                        (name) => t(name)
                                                                    )}
                                                                    disabled={isTranslationMode}
                                                                />
                                                            </div>

                                                            <div className="ms-auto">
                                                                <ErrorMessage name="types">
                                                                    {(msg) => (
                                                                        <div className="invalid-feedback d-block">
                                                                            {msg}
                                                                        </div>
                                                                    )}
                                                                </ErrorMessage>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-3 py-lg-2">
                                                        <Col sm={3} md={2} className="mb-2 mb-md-0">
                                                            <label
                                                                htmlFor="updateDish-category-field"
                                                                className="text-dark fs-7 fw-bold py-1 required-label"
                                                                onClick={handleLabelClick}
                                                            >
                                                                {t("Category")}
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={10}>
                                                            <div className="d-flex">
                                                                {pickedCategoryNames?.length > 0 ? (
                                                                    <div className="d-flex flex-wrap gap-3 me-4">
                                                                        {pickedCategoryNames.map(
                                                                            (category: IPickedCategoryNames) => {
                                                                                const categoryNameVm =
                                                                                    buildTranslationTextVm({
                                                                                        translatedValue: category.name,
                                                                                        originValue:
                                                                                            category.nameOrigin,
                                                                                        isTranslationMode,
                                                                                    });
                                                                                const parentNameVm =
                                                                                    buildTranslationTextVm({
                                                                                        translatedValue:
                                                                                            category.parentName,
                                                                                        originValue:
                                                                                            category.parentNameOrigin,
                                                                                        isTranslationMode,
                                                                                    });

                                                                                return (
                                                                                    <Badge
                                                                                        key={category.id}
                                                                                        bg="additional-grey"
                                                                                        className="rounded-1 px-2 d-flex align-items-center"
                                                                                    >
                                                                                        <span className="fs-16 mb-2 me-2 mb-lg-0 text-dark">
                                                                                            <span className="me-1">
                                                                                                {
                                                                                                    categoryNameVm.displayText
                                                                                                }
                                                                                            </span>
                                                                                            {categoryNameVm.isMissingTranslation ? (
                                                                                                <TranslationMissingDot
                                                                                                    tooltipText={
                                                                                                        translationTooltip
                                                                                                    }
                                                                                                />
                                                                                            ) : null}
                                                                                            {category.parentName ? (
                                                                                                <>
                                                                                                    <i className="bi bi-arrow-right mx-1 "></i>{" "}
                                                                                                    <span className="me-1">
                                                                                                        {
                                                                                                            parentNameVm.displayText
                                                                                                        }
                                                                                                    </span>
                                                                                                    {parentNameVm.isMissingTranslation ? (
                                                                                                        <TranslationMissingDot
                                                                                                            tooltipText={
                                                                                                                translationTooltip
                                                                                                            }
                                                                                                        />
                                                                                                    ) : null}
                                                                                                </>
                                                                                            ) : null}
                                                                                        </span>
                                                                                        <Button
                                                                                            className="bg-transparent border-0 text-dark p-0"
                                                                                            onClick={() =>
                                                                                                handleRemoveCategory(
                                                                                                    category.id
                                                                                                )
                                                                                            }
                                                                                            disabled={isTranslationMode}
                                                                                        >
                                                                                            <i className="bi bi-x-lg"></i>
                                                                                        </Button>
                                                                                    </Badge>
                                                                                );
                                                                            }
                                                                        )}
                                                                    </div>
                                                                ) : null}
                                                                <div>
                                                                    <Button
                                                                        ref={buttonCategoryRef}
                                                                        variant={`${touched.categories && errors.categories ? "outline-danger" : "outline-primary"}`}
                                                                        className="fw-normal py-1"
                                                                        id="updateDish-categories-field"
                                                                        style={{ width: "149px", maxHeight: "31px" }}
                                                                        onClick={() => {
                                                                            setIsMeMenuCategoryModalOpen(true);
                                                                        }}
                                                                        disabled={isTranslationMode}
                                                                    >
                                                                        {t("Select Category")}
                                                                    </Button>
                                                                    <ErrorMessage name="categories">
                                                                        {(msg) => (
                                                                            <div className="text-danger d-block fs-7 mt-1">
                                                                                {msg}
                                                                            </div>
                                                                        )}
                                                                    </ErrorMessage>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-3">
                                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="updateDish-description-field"
                                                                className="fs-7 fw-bold pt-sm-6"
                                                            >
                                                                {t("Description dish")}
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={10}>
                                                            <TextareaInputWithCounter
                                                                id="updateDish-description-field"
                                                                name="description"
                                                                maxLength={400}
                                                                inputCol={12}
                                                                inputColSm={12}
                                                                placeholder={
                                                                    descriptionVm.placeholderText ??
                                                                    t("Add description")
                                                                }
                                                                disabledLabel
                                                                rightSlot={
                                                                    descriptionVm.isMissingTranslation ? (
                                                                        <CopyOriginButton
                                                                            {...getCopyOriginProps(descriptionVm)}
                                                                            onCopied={() =>
                                                                                toggleShowToast(
                                                                                    t("Original text copied")
                                                                                )
                                                                            }
                                                                        />
                                                                    ) : null
                                                                }
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-3">
                                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="updateDish-composition-field"
                                                                className="fs-7 fw-bold text-break pt-sm-6"
                                                            >
                                                                {t("Short composition")}
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={10}>
                                                            <TextareaInputWithCounter
                                                                id="updateDish-composition-field"
                                                                name="composition"
                                                                maxLength={400}
                                                                inputCol={12}
                                                                inputColSm={12}
                                                                placeholder={
                                                                    compositionVm.placeholderText ??
                                                                    t("Add short composition")
                                                                }
                                                                rightSlot={
                                                                    compositionVm.isMissingTranslation ? (
                                                                        <CopyOriginButton
                                                                            {...getCopyOriginProps(compositionVm)}
                                                                            onCopied={() =>
                                                                                toggleShowToast(
                                                                                    t("Original text copied")
                                                                                )
                                                                            }
                                                                        />
                                                                    ) : null
                                                                }
                                                                disabledLabel
                                                            />
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-3">
                                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="updateDish-unit-field"
                                                                className="fs-7 fw-bold text-break required-label"
                                                            >
                                                                {t("Units of measurement")}
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={10}>
                                                            <ReactSelect
                                                                id="updateDish-unit-field"
                                                                name="unit"
                                                                options={unitOptions}
                                                                value={unitOptions.find(
                                                                    (option: Option) => option.value === values.unit
                                                                )}
                                                                onChange={(selectedOption: Option) => {
                                                                    setFieldValue("unit", selectedOption.value);
                                                                }}
                                                                placeholder={t("Select Unit of Measurement")}
                                                                isSearchable={false}
                                                                disabled={isTranslationMode}
                                                            />
                                                            <ErrorMessage name="unit">
                                                                {(msg) => (
                                                                    <div className="invalid-feedback d-block">
                                                                        {msg}
                                                                    </div>
                                                                )}
                                                            </ErrorMessage>
                                                        </Col>
                                                    </Row>
                                                </div>
                                                <FieldArray
                                                    name="portions"
                                                    render={({ remove, push }) => (
                                                        <div className="my-4 my-lg-5">
                                                            <h5 className="fs-24 mb-3 mb-lg-4">{t("Servings")}</h5>
                                                            {values?.portions?.map((_, index) => {
                                                                const nameId = `updateDish-portions.${index}.name-field`;
                                                                const volumeId = `updateDish-portions.${index}.volume-field`;
                                                                const priceId = `updateDish-portions.${index}.price-field`;
                                                                const portionOriginName =
                                                                    menuItem?.portions?.[index]?.nameOrigin ?? null;
                                                                const portionNameVm = buildTranslationTextVm({
                                                                    translatedValue: values.portions?.[index]?.name,
                                                                    originValue: portionOriginName,
                                                                    isTranslationMode,
                                                                });
                                                                return (
                                                                    <Row key={index} className="mb-3">
                                                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                                            <label
                                                                                htmlFor={nameId}
                                                                                className="fs-7 fw-bold py-sm-6 required-label"
                                                                            >
                                                                                {t("Serving name")}
                                                                            </label>
                                                                        </Col>
                                                                        <Col sm={9} md={10}>
                                                                            <Row>
                                                                                <Col
                                                                                    xs={12}
                                                                                    xxl={3}
                                                                                    className="mb-3 mb-xxl-0"
                                                                                >
                                                                                    <TextInput
                                                                                        name={`portions.${index}.name`}
                                                                                        id={nameId}
                                                                                        inputCol={12}
                                                                                        placeholder={
                                                                                            portionNameVm.placeholderText ??
                                                                                            "Serving name"
                                                                                        }
                                                                                        inputColSm={12}
                                                                                        isLabel={false}
                                                                                        label=""
                                                                                        maxLength={50}
                                                                                        rightSlot={
                                                                                            portionNameVm.isMissingTranslation ? (
                                                                                                <CopyOriginButton
                                                                                                    {...getCopyOriginProps(
                                                                                                        portionNameVm
                                                                                                    )}
                                                                                                    onCopied={() =>
                                                                                                        toggleShowToast(
                                                                                                            t(
                                                                                                                "Original text copied"
                                                                                                            )
                                                                                                        )
                                                                                                    }
                                                                                                />
                                                                                            ) : null
                                                                                        }
                                                                                    />
                                                                                </Col>
                                                                                <Col
                                                                                    xxl={4}
                                                                                    lg={5}
                                                                                    sm={12}
                                                                                    className="mb-3 mb-lg-0"
                                                                                >
                                                                                    <Row className="gx-2">
                                                                                        <Col xs={5}>
                                                                                            <label
                                                                                                htmlFor={volumeId}
                                                                                                className="fs-7 fw-bold required-label"
                                                                                            >
                                                                                                {t(
                                                                                                    "Weight/ Volume dish"
                                                                                                )}
                                                                                            </label>
                                                                                        </Col>
                                                                                        <Col xs={7}>
                                                                                            <PortionVolumeInput
                                                                                                name={`portions.${index}.volume`}
                                                                                                id={volumeId}
                                                                                                placeholder={"0.0"}
                                                                                                initialValue={
                                                                                                    values.portions[
                                                                                                        index
                                                                                                    ].volume
                                                                                                }
                                                                                                disabled={
                                                                                                    isTranslationMode
                                                                                                }
                                                                                            />
                                                                                        </Col>
                                                                                    </Row>
                                                                                </Col>
                                                                                <Col md={12} lg={7} xxl={5}>
                                                                                    <Row className="gx-2">
                                                                                        <Col xs={5} lg={3}>
                                                                                            <label
                                                                                                htmlFor={priceId}
                                                                                                className="fs-7 fw-bold pt-6 required-label"
                                                                                            >
                                                                                                {t("Price")}
                                                                                            </label>
                                                                                        </Col>
                                                                                        <Col
                                                                                            xs={7}
                                                                                            lg={9}
                                                                                            className="d-flex"
                                                                                        >
                                                                                            <PriceInput
                                                                                                name={`portions.${index}.price`}
                                                                                                placeholder="0.00"
                                                                                                id={priceId}
                                                                                                initialValue={
                                                                                                    values.portions[
                                                                                                        index
                                                                                                    ].price
                                                                                                }
                                                                                                classWrapperField="w-100"
                                                                                                disabled={
                                                                                                    isTranslationMode
                                                                                                }
                                                                                            />
                                                                                            {values.portions.length >
                                                                                                1 &&
                                                                                                index >= 1 && (
                                                                                                    <div className="ms-2">
                                                                                                        <Button
                                                                                                            type="button"
                                                                                                            variant="outline-secondary"
                                                                                                            className="w-100 px-3 py-6 fs-normal rounded-1 align-self-start"
                                                                                                            onClick={() =>
                                                                                                                remove(
                                                                                                                    index
                                                                                                                )
                                                                                                            }
                                                                                                            disabled={
                                                                                                                isTranslationMode
                                                                                                            }
                                                                                                        >
                                                                                                            <i className="bi bi-trash3 text-dark"></i>
                                                                                                        </Button>
                                                                                                    </div>
                                                                                                )}
                                                                                        </Col>
                                                                                    </Row>
                                                                                </Col>
                                                                                {index ===
                                                                                    values.portions.length - 1 && (
                                                                                    <Col xs={12} className="mt-3">
                                                                                        <Button
                                                                                            type="button"
                                                                                            variant="light"
                                                                                            className="fs-7 text-primary p-0 border-0 bg-transparent"
                                                                                            onClick={() =>
                                                                                                push({
                                                                                                    name: "",
                                                                                                    volume: "",
                                                                                                    price: "",
                                                                                                })
                                                                                            }
                                                                                            disabled={isTranslationMode}
                                                                                        >
                                                                                            <i className="bi bi-plus me-1"></i>
                                                                                            {t(
                                                                                                "Add another portion option"
                                                                                            )}
                                                                                        </Button>
                                                                                    </Col>
                                                                                )}
                                                                            </Row>
                                                                        </Col>
                                                                    </Row>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                />

                                                <h5 className="fs-24 mb-3 mb-lg-4">{t("Additional Information")}</h5>
                                                <fieldset disabled={isTranslationMode}>
                                                    <Row className="mb-3">
                                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="updateDish-cookingTime-field"
                                                                className="fs-7 fw-bold text-break py-sm-6"
                                                            >
                                                                {t("Cooking time")}
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={10}>
                                                            <ReactSelect
                                                                id="updateDish-cookingTime-field"
                                                                name="cookingTime"
                                                                options={cookingTimeOptions}
                                                                value={cookingTimeOptions.find(
                                                                    (option: OptionString) =>
                                                                        option.value === values.cookingTime
                                                                )}
                                                                onChange={(selectedOption: OptionString) => {
                                                                    setFieldValue("cookingTime", selectedOption.value);
                                                                }}
                                                                placeholder={t("Select cooking time")}
                                                                isSearchable={false}
                                                                disabled={isTranslationMode}
                                                            />
                                                            <ErrorMessage
                                                                name="cookingTime"
                                                                component="div"
                                                                className="invalid-feedback"
                                                            />
                                                            <div className="form-check mt-2 mb-0">
                                                                <Field
                                                                    type="checkbox"
                                                                    name="isLongCookingTime"
                                                                    id="updateDish-isLongCookingTime-field"
                                                                    className="form-check-input"
                                                                    checked={values.isLongCookingTime}
                                                                />
                                                                <label
                                                                    className="form-check-label fs-7"
                                                                    htmlFor="updateDish-isLongCookingTime-field"
                                                                >
                                                                    {t('Display icon "long cooking"')}
                                                                </label>
                                                                <OverlayTrigger
                                                                    overlay={
                                                                        <Tooltip>
                                                                            {t(
                                                                                "There will be a marker next to this dish in the menu"
                                                                            )}
                                                                        </Tooltip>
                                                                    }
                                                                >
                                                                    <i className="bi bi-info-circle ms-1 fs-7 pt-1"></i>
                                                                </OverlayTrigger>
                                                                <ErrorMessage
                                                                    name="isLongCookingTime"
                                                                    component="div"
                                                                    className="text-danger"
                                                                />
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="updateDish-markers-field"
                                                                className="fs-7 fw-bold py-sm-6"
                                                            >
                                                                {t("Warning")}
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={10}>
                                                            <div className="d-flex justify-content-start align-content-center flex-wrap border rounded-1 p-6 gap-3">
                                                                <MultiSwitchField
                                                                    name="markers"
                                                                    options={(menuMarkersData?.menuMarkers ?? []).map(
                                                                        (marker) => ({
                                                                            value: marker.id,
                                                                            label: marker.name,
                                                                        })
                                                                    )}
                                                                />
                                                            </div>
                                                            <small className="text-grey">
                                                                {t("Optional small icons placed next to the dish name")}
                                                            </small>
                                                        </Col>
                                                    </Row>
                                                    <Row className="mb-3">
                                                        <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                            <label
                                                                htmlFor="updateDish-allergens-field"
                                                                className="fs-7 fw-bold py-sm-6"
                                                            >
                                                                {t("Allergen list")}
                                                            </label>
                                                        </Col>
                                                        <Col sm={9} md={10}>
                                                            <div className="d-flex justify-content-start flex-wrap border rounded-1 p-6 gap-3">
                                                                <MultiSwitchField
                                                                    name="allergens"
                                                                    options={(
                                                                        menuAllergensData?.menuAllergens ?? []
                                                                    ).map((allergen, index) => ({
                                                                        value: allergen.id,
                                                                        label: (
                                                                            <>
                                                                                <span className="fs-7 text-primary-grey me-1">
                                                                                    {index + 1}
                                                                                </span>
                                                                                <span className="text-grey">
                                                                                    ({allergen.name})
                                                                                </span>
                                                                            </>
                                                                        ),
                                                                    }))}
                                                                />
                                                            </div>
                                                            <small className="text-grey">
                                                                {t(
                                                                    "Small numbers placed next to the dish name. Food ingredients that must be declared as allergens in the EU"
                                                                )}
                                                            </small>
                                                        </Col>
                                                    </Row>
                                                </fieldset>

                                                <Row className="mb-4 mb-lg-5">
                                                    <Col sm={3} md={2} className="mb-2 mb-sm-0">
                                                        <label
                                                            htmlFor="updateDish-supplements-field"
                                                            className="fs-7 fw-bold"
                                                        >
                                                            {t("List of options/additives")}
                                                        </label>
                                                    </Col>
                                                    <Col sm={9} md={10}>
                                                        <div className="d-flex align-items-center w-100 mb-3">
                                                            <div className="w-100">
                                                                <DishSupplementsInput
                                                                    id="updateDish-supplements-field"
                                                                    selectedIds={values.supplements || []}
                                                                    groups={supplementGroupsForInput}
                                                                    onRemove={(groupId) => {
                                                                        const updated = (
                                                                            values.supplements || []
                                                                        ).filter((id) => id !== groupId);
                                                                        setFieldValue("supplements", updated);
                                                                    }}
                                                                    onEdit={(groupId) => {
                                                                        if (canEditSupplement) openEditModal(groupId);
                                                                    }}
                                                                    disabled={isTranslationMode}
                                                                    canEditSupplement={canEditSupplement}
                                                                />
                                                            </div>
                                                            <Button
                                                                onClick={() => setShowSupplementMenu(true)}
                                                                type="button"
                                                                variant="outline-secondary"
                                                                className="h-100 ms-3 px-3 py-6 fs-normal rounded-1 align-self-start"
                                                                disabled={isTranslationMode}
                                                            >
                                                                <i className="bi bi-plus-lg text-dark"></i>
                                                            </Button>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col lg={3} className="ms-auto ps-lg-0">
                                                <h5 className="fs-24 text-capitalize mb-4">{t("Photo dish")}</h5>
                                                <ImageCrop
                                                    previewPhoto={menuItem?.favicon}
                                                    setFieldValue={setFieldValue}
                                                    fieldName="image"
                                                    modalTitle={t("Dish Photo")}
                                                    notBGinfo
                                                    isMenuInfo
                                                    fieldSetter={() => setFieldValue("isUpdateImage", true)}
                                                    dismissImage={() => {
                                                        setFieldValue("isUpdateImage", true);
                                                        setFieldValue("image", null);
                                                    }}
                                                    disabled={isTranslationMode}
                                                />
                                                <ErrorMessage name="image">
                                                    {(msg) => <div className="invalid-feedback d-block">{msg}</div>}
                                                </ErrorMessage>
                                            </Col>
                                        </Row>
                                        <div className="d-sm-flex my-4 mt-lg-0">
                                            <Button
                                                id={"updateDish-cancel-button"}
                                                onClick={() => {
                                                    if (location.state?.menuId) {
                                                        navigate(
                                                            `/menu/page/${location.state.menuId}/branch/${branchId}`
                                                        );
                                                    } else {
                                                        navigate(`/menu/dishes/branch/${branchId}`);
                                                    }
                                                }}
                                                variant="outline-primary"
                                                className="fw-normal mb-3 mb-sm-0 me-3 custom-btn rounded-1"
                                            >
                                                {t("Cancel")}
                                            </Button>
                                            <Button
                                                id={"updateDish-submit-button"}
                                                type="submit"
                                                variant="primary"
                                                disabled={isSubmitting}
                                                className="fw-bold custom-btn rounded-1"
                                            >
                                                {isSubmitting ? (
                                                    <SpinnerLoader variant="light" loadingText={submitLoadingText} />
                                                ) : (
                                                    submitText
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                    <AddSupplementsToDishModal
                                        show={showSupplementMenu}
                                        onClose={() => {
                                            setShowSupplementMenu(false);
                                        }}
                                        onCreateNew={() => {
                                            if (!canCreateSupplement) return;
                                            setShowSupplementMenu(false);
                                            openCreateModal(true);
                                        }}
                                        initialSelectedIds={values.supplements || []}
                                        onApply={(selectedIds) => {
                                            setFieldValue("supplements", selectedIds);
                                        }}
                                        onRefetchReady={setListAddOnsRefetch}
                                        versionLocale={selectedLangVersionCode}
                                        canCreateNewSupplement={canCreateSupplement}
                                    />

                                    {modalState.isOpen && dishesData && (
                                        <SupplementsGroupModal
                                            show={modalState.isOpen}
                                            handleClose={handleCloseModal}
                                            onSave={handleSubmitSupplementsGroupModal}
                                            dishesData={dishesData}
                                            existingGroup={modalState.group || undefined}
                                            mode={modalState.mode}
                                            primaryLanguage={primaryLanguageVm}
                                            selectedLanguage={selectedLanguageVm}
                                        />
                                    )}
                                    <ConfirmDisabledMenuTypesModal
                                        show={availabilityConfirmState.show}
                                        menuTypeLabels={availabilityConfirmState.menuTypeLabels}
                                        confirmText={submitText}
                                        onCancel={() => resolveAvailabilityConfirmation(false)}
                                        onConfirm={() => resolveAvailabilityConfirmation(true)}
                                    />
                                </>
                            )}

                            {isMeMenuCategoryModalOpen && meMenucategoriesData && !meMenuCategoryLoading ? (
                                <ServiceModalWrapper
                                    companyId={Number(currentCompanyId)}
                                    branchId={Number(branchId)}
                                    i18n={i18next}
                                    selectedCategoryId={null}
                                    selectedSubcategoryId={null}
                                    apolloUrl={process.env.REACT_APP_API_URL}
                                    isOpen={isMeMenuCategoryModalOpen}
                                    onClose={() => {
                                        setIsMeMenuCategoryModalOpen(!isMeMenuCategoryModalOpen);
                                    }}
                                    onCategorySelect={(selectedCategory) => {
                                        if (selectedCategory) {
                                            setPickedCategoryIds((prev) => {
                                                if (!prev.includes(selectedCategory.id)) {
                                                    return [...prev, selectedCategory.id];
                                                }
                                                return prev;
                                            });

                                            setPickedCategoryNames((prev: any) => {
                                                if (!prev.some((cat: any) => cat.id === selectedCategory.id)) {
                                                    return [
                                                        ...prev,
                                                        {
                                                            id: selectedCategory.id,
                                                            name: selectedCategory.name,
                                                            parentName: null,
                                                            nameOrigin: selectedCategory.nameOrigin ?? null,
                                                            parentNameOrigin: null,
                                                        },
                                                    ];
                                                }
                                                return prev;
                                            });
                                        }
                                        setIsMeMenuCategoryModalOpen(!isMeMenuCategoryModalOpen);
                                    }}
                                    onSubcategorySelect={(selectedSubcategory) => {
                                        if (selectedSubcategory) {
                                            setPickedCategoryIds((prev) => {
                                                if (!prev.includes(selectedSubcategory.id)) {
                                                    return [...prev, selectedSubcategory.id];
                                                }
                                                return prev;
                                            });

                                            setPickedCategoryNames((prev: any) => {
                                                if (!prev.some((cat: any) => cat.id === selectedSubcategory.id)) {
                                                    return [
                                                        ...prev,
                                                        {
                                                            id: selectedSubcategory.id,
                                                            name: selectedSubcategory.name,
                                                            parentName:
                                                                selectedSubcategory?.parentCategory?.name || null,
                                                            nameOrigin: selectedSubcategory.nameOrigin ?? null,
                                                            parentNameOrigin:
                                                                selectedSubcategory?.parentCategory?.nameOrigin ?? null,
                                                        },
                                                    ];
                                                }
                                                return prev;
                                            });
                                        }
                                        setIsMeMenuCategoryModalOpen(!isMeMenuCategoryModalOpen);
                                    }}
                                    categoryList={categoryList}
                                    categoryInput={categoryInput}
                                    categoryListData={categoryListData}
                                    refetchCategories={refetchCategories}
                                    onSubmitCreate={handleSubmitCreateCategory}
                                    canCreateCategory={canCreateDishCategory}
                                    currentCategory={currentCategory}
                                    categoryService={CategoryMeMenu}
                                    requestInputService={RequestInputService}
                                    normalizeCategoryData={normalizeCategoryData}
                                    renderCategoryIconPicker={({ value, onChange, disabled }) => (
                                        <CategoryIconPickerWithLabel
                                            icons={categoryIcons}
                                            value={value}
                                            onChange={onChange}
                                            disabled={disabled}
                                            basePath={process.env.REACT_APP_POS_URL}
                                        />
                                    )}
                                />
                            ) : null}
                            {!!selectedLangVersionCode && selectedLanguageVm.label ? (
                                <ModalCloneMenuItem
                                    showCloneModal={showCloneModal}
                                    onCloseModal={() => setShowCloneModal(false)}
                                    setShowCloneModal={setShowCloneModal}
                                    currentDishId={currentDishId}
                                    refetchMenuItems={refetchMenuItems}
                                    localeVersion={selectedLangVersionCode}
                                    headerLanguage={selectedLanguageVm.label}
                                    canCloneDish={canCloneDish}
                                    isTranslationMode={isTranslationMode}
                                />
                            ) : null}
                        </>
                    );
                }}
            </Formik>
            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default DishUpdatePage;
